import React, { useMemo } from 'react';
import { MapPin, Star, ArrowRight, AlertCircle } from 'lucide-react';

export default function HotelCard({ hotel, onSelectHotel, searchData }) {
  
  // ✅ Calculate nights
  const calculateNights = useMemo(() => {
    if (!searchData?.checkIn || !searchData?.checkOut) return 1;
    
    const parseDate = (dateStr) => {
      if (dateStr instanceof Date) return dateStr;
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return new Date(dateStr);
    };
    
    const checkIn = parseDate(searchData.checkIn);
    const checkOut = parseDate(searchData.checkOut);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays || 1;
  }, [searchData]);
  
  // Get lowest price from all pensions (this is TOTAL for all nights)
  const getLowestPrice = () => {
    if (!hotel.pensions || hotel.pensions.length === 0) return 0;
    const prices = hotel.pensions.map(p => p.minPrice || 0);
    return Math.min(...prices);
  };

  const lowestTotalPrice = getLowestPrice();
  const lowestPricePerNight = lowestTotalPrice / calculateNights;  // ✅ Divide to get per night

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
         onClick={() => onSelectHotel(hotel)}>
      <div className="flex flex-col md:flex-row">
        {/* Hotel Image */}
        <div className="relative w-full md:w-80 h-64 md:h-auto overflow-hidden group">
          <img 
            src={hotel.image} 
            alt={hotel.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          {/* Recommended Badge (if applicable) */}
          {hotel.rawData?.Recommended > 0 && (
            <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
              Recommandé
            </div>
          )}

          {/* ✅ Fallback Availability Badge */}
          {hotel.availabilityType === 'fallback' && (
            <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
              <AlertCircle size={14} />
              Dates alternatives disponibles
            </div>
          )}
        </div>

        {/* Hotel Details */}
        <div className="flex-1 p-6 flex flex-col">
          {/* Hotel Name and Stars */}
          <div className="mb-4">
            <h3 className="text-2xl font-bold text-[#2B4979] hover:text-[#FF7B47] transition mb-2">
              {hotel.name}
            </h3>
            
            {/* Stars */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1">
                {[...Array(hotel.stars || 3)].map((_, i) => (
                  <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              {hotel.starCategory && (
                <span className="text-sm text-gray-500">({hotel.starCategory})</span>
              )}
            </div>
            
            {/* Address/Location */}
            <div className="flex items-start gap-2 text-gray-600 mb-3">
              <MapPin size={16} className="text-[#FF7B47] mt-1 flex-shrink-0" />
              <span className="text-sm">
                {hotel.rawData?.Hotel?.Adress || hotel.location}
              </span>
            </div>
          </div>

          {/* Themes/Tags */}
          {hotel.rawData?.Hotel?.Theme && hotel.rawData.Hotel.Theme.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {hotel.rawData.Hotel.Theme.slice(0, 5).map((theme, idx) => (
                  <span 
                    key={idx}
                    className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full border border-blue-200"
                  >
                    {theme}
                  </span>
                ))}
                {hotel.rawData.Hotel.Theme.length > 5 && (
                  <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                    +{hotel.rawData.Hotel.Theme.length - 5}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Bottom Section - Price and Button */}
          <div className="mt-auto pt-4 border-t flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm text-gray-600 mb-1">À partir de:</span>
              <span className="text-3xl font-bold text-[#FF7B47]">
                {lowestTotalPrice.toFixed(3)} TND  <span className="text-xs text-gray-400">pour {calculateNights} nuit{calculateNights > 1 ? 's' : ''} </span>
              </span>
              
              <span className="text-xs text-gray-400 mt-1">
                 {lowestPricePerNight.toFixed(3)} TND / nuit
              </span>
            </div>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelectHotel(hotel);
              }}
              className="bg-[#FF7B47] hover:bg-[#ff6a30] text-white px-6 py-3 rounded-lg transition font-semibold shadow-md hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
            >
              Voir détails
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
